import React, { useState, useEffect } from "react";
import Parser from "html-react-parser";
import Modal from "../shared/Modal";
import CustomButton from "../shared/CustomButton";
import { CloseMD, t } from "../normalizer";

// Reusable confirmation modal for voucher actions (reorder / archive).
// Three-state machine driven by `response`, mirroring Profile/Card/Popup.js:
//   "0"   → confirmation prompt (backend string); Confirm + Cancel
//   "200" → success callout (backend message); OK navigates home
//   other → warning callout (backend error); OK closes the modal
//
// The parent owns the API call via `onConfirm`, which returns the service
// promise so this component can read the resulting status/message.
const VoucherConfirmModal = (props) => {
  const {
    show,
    title,
    promptMessage,
    confirmLabel,
    onConfirm,
    onClose,
    onSuccessOk
  } = props;

  const [response, setResponse] = useState("0");
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);

  // Reset to the prompt state every time the modal is (re)opened.
  useEffect(() => {
    if (show) {
      setResponse("0");
      setMessage("");
      setPending(false);
    }
  }, [show]);

  if (!show) return null;

  const readMessage = (data) =>
    (data && (data.message || data.error)) || "";

  const handleConfirm = () => {
    setPending(true);
    onConfirm()
      .then((res) => {
        setResponse(String(res.status));
        setMessage(readMessage(res.data));
        setPending(false);
      })
      .catch((error) => {
        const res = error && error.response;
        setResponse(res ? String(res.status) : "-1");
        setMessage(res ? readMessage(res.data) : "");
        setPending(false);
      });
  };

  const isPrompt = response === "0";
  const isSuccess = response === "200";

  const handleOk = isSuccess ? onSuccessOk : onClose;

  return (
    <Modal show={show} untoggle={onClose} small>
      <div className="gl2026-modal__header noborder">
        <h4 className="gl2026-modal__title">{title}</h4>
        <button
          type="button"
          className="gl2026-modal__close"
          onClick={onClose}
          aria-label={t("gamification2026.voucher.modal.close")}
        >
          <CloseMD />
        </button>
      </div>

      <div className="gl2026-modal__body">
        {isPrompt && (
          <div className="gl2026-modal__callout gl2026-modal__callout--warning">
            <div className="gl2026-modal__callout-content">
              <p className="gl2026-modal__callout-text">
                {promptMessage ? Parser(promptMessage) : title}
              </p>
            </div>
          </div>
        )}

        {isSuccess && (
          <div className="gl2026-modal__alert gl2026-modal__alert--success">
            {message ? Parser(message) : t("gamification2026.voucher.modal.success")}
          </div>
        )}

        {!isPrompt && !isSuccess && (
          <div className="gl2026-modal__callout gl2026-modal__callout--warning">
            <div className="gl2026-modal__callout-content">
              <p className="gl2026-modal__callout-text">
                {message ? Parser(message) : t("gamification2026.voucher.modal.error")}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="gl2026-modal__footer">
        {isPrompt && (
          <CustomButton secondary type="button" onClick={onClose}>
            {t("gamification2026.voucher.modal.cancel")}
          </CustomButton>
        )}
        {isPrompt && (
          <CustomButton type="button" disabled={pending} onClick={handleConfirm}>
            {confirmLabel || t("gamification2026.voucher.modal.confirm")}
          </CustomButton>
        )}
        {!isPrompt && (
          <CustomButton type="button" onClick={handleOk}>
            {t("gamification2026.voucher.modal.ok")}
          </CustomButton>
        )}
      </div>
    </Modal>
  );
};

export default VoucherConfirmModal;
